import { computed, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import {
  injectTellentAppKind,
  TellentGetIconParams,
  TellentNavigationGroup,
  TellentNavigationIcon,
  TellentNavigationItem,
  TellentNavigationItemUpgrade,
  TellentNavigationLink,
  TellentNavigationSection,
  TellentNavigationSeparator,
  TellentNavigationViewModel,
  TellentRoute,
} from '@core/tellent-types';
import { findFirstLink, injectTellentAppUrlBuilder } from '@core/tellent-util';

import { TellentAppShellStateService } from '../app-shell/app-shell-state.service';
import { TellentCurrentUserService } from '../tellent-current-user.service';
import { flipActiveIcon, getNavItemIconPair } from './navigation-helpers';
import { TellentNavigationRoutingService } from './navigation-routing.service';
import { injectTellentNavigationItems } from './navigation.providers';

type ID = string;
type TellentNavigationGroupSelected = TellentNavigationGroup | null | false;
type TellentRouteMapping = Record<ID, TellentRoute>;

type Context = {
  routes: TellentRouteMapping;
  location: string;
  userSelectedGroup: TellentNavigationGroupSelected;
  isMobile: boolean;
  hasMultipleSections: boolean;
};

@Injectable()
export class TellentNavigationViewModelBuilder {
  #hostAppKind = injectTellentAppKind();
  #tellentAppUrlBuilder = injectTellentAppUrlBuilder();
  #navRoutingSvc = inject(TellentNavigationRoutingService);
  #shellSvc = inject(TellentAppShellStateService);
  #currentUserDataSvc = inject(TellentCurrentUserService);

  public data = injectTellentNavigationItems();

  public userSelectedGroup = signal<TellentNavigationGroupSelected>(false);
  public currentUser = toSignal(this.#currentUserDataSvc.me$);

  public viewModel = computed<TellentNavigationViewModel>(() => {
    const items = this.data();
    const routes = Object.fromEntries(this.#navRoutingSvc.recognizedRoutes().map(r => [r.id, r]));
    const location = this.#navRoutingSvc.location();
    const userSelectedGroup = this.userSelectedGroup();
    const isMobile = this.#shellSvc.isMobile();
    const isMainView = this.#shellSvc.view() === 'main';

    const context: Context = {
      routes,
      location,
      userSelectedGroup,
      isMobile,
      hasMultipleSections: isMainView ? items.length > 1 : true,
    };

    const { hasMultipleSections } = context;

    const children = this.buildItems(items, context);
    const active = this.findActiveLink(children);

    return {
      active,
      children,
      hasMultipleSections,
    };
  });

  private buildItems(
    items: TellentNavigationItem[],
    context: Context,
    isChild: boolean = false,
  ): TellentNavigationItem[] {
    return items
      .map(item => {
        switch (item.type) {
          case 'link':
            return this.buildLinkItem(item, context, isChild);

          case 'group':
            return this.buildGroupItem(item, context, isChild);

          case 'section':
            return this.buildSectionItem(item, context, isChild);

          case 'separator':
            return this.buildSeparatorItem(item, context, isChild);

          default:
            return item;
        }
      })
      .filter(item => {
        if (!context.isMobile) return true;

        switch (item.type) {
          case 'group':
            return item.children.length;

          case 'link':
          case 'separator':
            return item.availableOnMobile;

          default:
            return true;
        }
      });
  }

  private buildSectionItem(
    item: TellentNavigationSection,
    context: Context,
    isChild: boolean,
  ): TellentNavigationSection {
    return {
      ...item,
      children: this.buildItems(item.children, context, isChild),
    };
  }

  private buildGroupItem(
    item: TellentNavigationGroup,
    context: Context,
    isChild: boolean,
  ): TellentNavigationGroup {
    const children = this.buildItems(
      context.isMobile
        ? item.children.filter(child => child.type === 'link' && child.availableOnMobile)
        : item.children,
      context,
      true,
    );

    const { userSelectedGroup } = context;

    const isActive = children.some(child => child.isActive);

    const isExpanded =
      userSelectedGroup === null
        ? false
        : userSelectedGroup === false
          ? isActive
          : userSelectedGroup.id === item.id;

    const firstLink = findFirstLink(children);

    const icon = firstLink
      ? this.getIcon({
          appKind: firstLink.appKind,
          id: firstLink.page.id,
          icon: item.icon,
          iconSvg: item.iconSvg,
          isChild,
          isActive,
        })
      : { icon: null, iconSvg: '' };

    return {
      ...item,
      ...icon,
      isExpanded,
      isChild,
      isActive,
      children,
    };
  }

  private buildLinkItem(
    item: TellentNavigationLink,
    context: Context,
    isChild: boolean,
  ): TellentNavigationLink {
    const recognizedRoute = context.routes[item.page.id];
    const path = this.buildPath(item.appKind, item.page, context);

    const upgrade = item.upgrade
      ? <TellentNavigationItemUpgrade>{
          ...item.upgrade,
          path: this.buildPath(item.appKind, item.upgrade.page, context),
        }
      : undefined;

    const isActive = this.isLinkItemActive(item, context, upgrade);

    const testId = recognizedRoute?.testId ?? item.page.id;
    const walkthroughId = recognizedRoute?.walkthroughId;

    return {
      ...item,
      ...(upgrade ? { upgrade } : {}),
      ...this.getIcon({
        appKind: item.appKind,
        id: item.page.id,
        icon: item.icon,
        iconSvg: item.iconSvg,
        isChild,
        isActive,
      }),
      testId,
      walkthroughId,
      path,
      isChild,
      isActive,
    };
  }

  private buildSeparatorItem(
    item: TellentNavigationSeparator,
    context: Context,
    isChild: boolean,
  ): TellentNavigationSeparator {
    return {
      ...item,
      isChild,
      isActive: false,
    };
  }

  private buildPath(
    appKind: TellentNavigationLink['appKind'],
    page: TellentNavigationLink['page'],
    context: Context,
  ) {
    const recognizedRoute = context.routes[page.id];
    const path =
      recognizedRoute && this.isHostAppKind(appKind)
        ? recognizedRoute.buildPath(page.params)
        : this.#tellentAppUrlBuilder(appKind, page, this.currentUser()?.orgGuid);

    return path;
  }

  private getIcon(params: TellentGetIconParams): TellentNavigationIcon {
    const { appKind, id, iconSvg, isChild, isActive } = params;
    if (isChild) {
      return {
        icon: 'nested',
        iconSvg: null,
      };
    }

    if (iconSvg) {
      return {
        icon: null,
        iconSvg,
      };
    }

    return { icon: flipActiveIcon(getNavItemIconPair(appKind, id), isActive)!, iconSvg: null };
  }

  private isHostAppKind(appKind: TellentNavigationLink['appKind']) {
    return appKind === this.#hostAppKind;
  }

  private isLinkItemActive(
    { page, appKind }: TellentNavigationLink,
    context: Context,
    upgrade?: TellentNavigationItemUpgrade,
  ): boolean {
    const recognizedRoute = context.routes[page.id];
    if (!recognizedRoute || !this.isHostAppKind(appKind)) return false;

    const path = this.buildPath(appKind, page, context);

    const matches = [recognizedRoute.matchPath(context.location, path)];

    if (upgrade) {
      const recognizedUpgradeRoute = context.routes[upgrade.page.id];

      if (recognizedUpgradeRoute) {
        matches.push(recognizedRoute.matchPath(context.location, upgrade.path));
      }
    }

    return matches.some(match => match);
  }

  private findActiveLink(items: TellentNavigationItem[]): TellentNavigationLink | null {
    for (const item of items) {
      if (item.isActive && item.type === 'link') {
        return item;
      } else if (item.type === 'group') {
        const activeChild = this.findActiveLink(item.children);
        if (activeChild) {
          return activeChild;
        }
      }
    }

    return null;
  }
}
